import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@design-system/Skeleton';
import { ConnectionCard } from '@design-system/Cards/ConnectionCard';
import { useGetMyDiseaseRiskListV2 } from '@viome/viome-base/src/usecase/Health/HealthQuery';
import { EventTracker } from '@/utils';
import { InfoSheet } from './InfoSheet';
import { INFO_SHEET_CONTENT } from '@/utils/InfoSheet/InfoSheetContent';

interface DiseaseRiskSectionProps {
  isQueryLoading: (isLoading: boolean) => void;
}

const cleanConnectionType = (input: string): string =>
  input.replace(/\bAssociated\b/g, '').replace(/\s+/g, '');

export const DiseaseRiskSection = ({ isQueryLoading }: DiseaseRiskSectionProps) => {
  const navigate = useNavigate();
  const {
    data: diseaseRiskListData,
    isLoading,
    isError,
  } = useGetMyDiseaseRiskListV2();
  const { connections = [] } = diseaseRiskListData || {};

  useEffect(() => {
    isQueryLoading(isLoading);
  }, [isLoading, isQueryLoading]);

  const navigateToConditionsPage = (name: string, risk: string) => {
    EventTracker.trackEvent(
      'health',
      `health_diseaseRisk_${name}_${cleanConnectionType(risk)}`,
    );
    navigate(`/health/connections/${encodeURIComponent(name)}`);
  };

  if (isLoading) {
    return (
      <div className="mt-12" data-testid="disease-risk-section-loading">
        <Skeleton className="h-[36px] w-[50%] mb-4 rounded-lg" />
        <Skeleton className="h-[20px] w-[80%] mb-4 rounded-lg" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton
              key={`skeleton-${index}`}
              className="h-[100px] w-full rounded-lg"
            />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !connections.length) {
    return null;
  }

  return (
    <div className="mt-12" data-testid="disease-risk-section">
      <h2 className="dark typography-h1 text-primary mb-2" data-testid="disease-risk-title">
        My Disease Risk
      </h2>
      <p className="dark typography-body text-secondary mb-6 lg:w-[35rem]" data-testid="disease-risk-description">
        Disease Risk represents your risk for a particular condition relative
        to the general population based on associated gene expression
        pathways. The risk level represents statistical probability, not a
        clinical diagnosis. All findings must be confirmed through standard
        diagnostic evaluations by a healthcare professional.
        <InfoSheet
          title={INFO_SHEET_CONTENT.diseaseRisk.title}
          description={INFO_SHEET_CONTENT.diseaseRisk.description}
        />
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="disease-risk-connectionsGrid">
        {connections.map((connection) => (
          <div key={connection.name} data-testid={`disease-risk-connectionCard-${connection.name.replace(/\s+/g, '-').toLowerCase()}`}>
            <ConnectionCard
              name={connection.name}
              noOfConnections={connection.no_of_connections}
              connectionType={connection.connection_type}
              connectionTypeColor={connection.connection_type_color}
              isProUser
              onClick={() =>
                navigateToConditionsPage(connection.name, connection.connection_type)
              }
            />
          </div>
        ))}
      </div>
    </div>
  );
};
